/**
 * Lightweight client-side heuristics for behavioral risk signals.
 *
 * NOT a moderation engine — the authoritative classifier is RAELA's
 * server-side moderate-message pipeline. These heuristics exist purely
 * to enqueue early *behavioral* signals (off-platform contact attempts,
 * financial requests, meet-now pressure) so the safety system can:
 *
 *   1. surface a real-time soft nudge back to the sender
 *   2. accrue weight toward the 7-day risk score
 *   3. auto-escalate to the mod queue when threshold is crossed
 *
 * Detectors return `null` for clean text or a list of detected kinds.
 */
import type { BehavioralSignalKind } from "./useBehavioralSignals";

// Off-platform contact attempts — covers WhatsApp/Telegram/Signal/Snap/IG/email/phone.
const OFF_PLATFORM_PATTERNS: RegExp[] = [
  /\b(whats\s?app|wa\.me|telegram|t\.me|signal|snap(chat)?|kik|wickr|wechat)\b/i,
  /\b(insta(gram)?|ig)\s*(handle|@|me|dm)?\b/i,
  /\b(my|your)\s*(number|phone|cell|mobile)\b/i,
  /\b(text|call)\s*me\s*(at|on)?\b/i,
  // Phone number patterns (loose — international and US)
  /(?:\+?\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3,4}[\s.-]?\d{4}/,
  // Email
  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i,
];

// Financial / payment requests — high-confidence scam indicator.
const FINANCIAL_PATTERNS: RegExp[] = [
  /\b(venmo|cashapp|cash\s*app|zelle|paypal|wise|revolut|wire\s*transfer)\b/i,
  /\b(bitcoin|btc|ethereum|eth|crypto|usdt|usdc|wallet\s*address)\b/i,
  /\b(gift\s*card|amazon\s*card|itunes\s*card|steam\s*card)\b/i,
  /\b(send|wire|transfer|loan|borrow|need)\s*(me)?\s*\$?\s*\d{2,}\b/i,
  /\$\s?\d{2,}/,
  /\b(western\s*union|moneygram)\b/i,
];

// Meet-now / pressure language — rushing, address, "tonight".
const MEET_NOW_PATTERNS: RegExp[] = [
  /\b(come\s*over|come\s*by|head\s*over|swing\s*by)\s*(now|tonight|right\s*now)?\b/i,
  /\b(my\s*place|your\s*place|hotel|airbnb)\s*(now|tonight)\b/i,
  /\b(send|share|drop)\s*(me)?\s*your?\s*(address|location|pin)\b/i,
  /\bwhat'?s\s*your\s*address\b/i,
];

// Sexual pressure — coercive language. Lightweight; the real classifier is server-side.
const SEXUAL_PRESSURE_PATTERNS: RegExp[] = [
  /\b(send|show)\s*(me)?\s*(nudes?|pics?|photos?|something\s*sexy)\b/i,
  /\b(let'?s\s*sext|wanna\s*(fuck|hookup)\s*(now|tonight)?)\b/i,
  /\bcome\s*on,?\s*don'?t\s*be\s*(shy|boring|prude)\b/i,
];

export interface DetectedSignal {
  kind: BehavioralSignalKind;
  evidence: { match: string; pattern_group: string };
}

const scan = (
  text: string,
  patterns: RegExp[],
  kind: BehavioralSignalKind,
  group: string,
): DetectedSignal | null => {
  for (const re of patterns) {
    const m = text.match(re);
    if (m) {
      return { kind, evidence: { match: m[0].slice(0, 120), pattern_group: group } };
    }
  }
  return null;
};

/**
 * Detect all signal kinds present in a single outbound text message.
 * Returns at most one of each kind — order matters for severity.
 */
export const detectMessageSignals = (text: string): DetectedSignal[] => {
  if (!text || text.trim().length < 3) return [];
  const signals: DetectedSignal[] = [];
  const fin = scan(text, FINANCIAL_PATTERNS, "financial_request", "financial");
  if (fin) signals.push(fin);
  const off = scan(text, OFF_PLATFORM_PATTERNS, "off_platform_request", "off_platform");
  if (off) signals.push(off);
  const meet = scan(text, MEET_NOW_PATTERNS, "meet_now_pressure", "meet_now");
  if (meet) signals.push(meet);
  const sex = scan(text, SEXUAL_PRESSURE_PATTERNS, "sexual_pressure", "sexual_pressure");
  if (sex) signals.push(sex);
  return signals;
};

/**
 * Detect rapid-messaging pattern from recent send timestamps.
 * Default: 5+ sends within 10 seconds OR 12+ within 60 seconds.
 */
export const detectRapidMessaging = (
  timestamps: number[],
  now: number = Date.now(),
): DetectedSignal | null => {
  if (timestamps.length < 5) return null;
  const last10s = timestamps.filter((t) => now - t < 10_000).length;
  const last60s = timestamps.filter((t) => now - t < 60_000).length;
  if (last10s >= 5 || last60s >= 12) {
    return {
      kind: "rapid_messaging",
      evidence: { match: `${last10s}/10s ${last60s}/60s`, pattern_group: "velocity" },
    };
  }
  return null;
};
